import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoSaveResult } from '@nkr/models/opo-document';
import { IOpoPortalUsersData, IOpoUserAddRequest, IOpoUserUpdateRequest } from '@nkr/models/opo-portal-users';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { UserActionCreators } from '@nkr/store/users';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

interface IRetVal {
    data: IOpoPortalUsersData | null,
    loading: boolean,
    error: string,
    isEmailAvailable: (email: string) => Promise<boolean>,
    addUser: (request: IOpoUserAddRequest) => Promise<IOpoSaveResult>,
    resendVerification: (userId: number) => Promise<IOpoSaveResult>,
    updateUser: (userId: number, request: IOpoUserUpdateRequest) => Promise<IOpoSaveResult>,
    deactivateUser: (userId: number) => Promise<IOpoSaveResult>
}

const useOpoPortalUsers = (): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [data, setData] = useState<IOpoPortalUsersData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const baseUrl = `${__API__}/opo/${opoId}/users`;

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync('Getting portal users, one moment please...'));

            try {
                const response: ITypedBaseResponse<IOpoPortalUsersData> = await dispatch(AuthActionCreators.apiGet({ url: baseUrl }));

                if (!isCurrent) {
                    return;
                }

                if (response && response.data.success) {
                    setData(response.data.results);
                } else {
                    setError('Unable to load portal users');
                }
            } catch (err) {
                if (isCurrent) {
                    setError('Unable to load portal users');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId, refreshKey]);

    const runAction = async (message: string, action: () => Promise<ITypedBaseResponse<null>>, failureMessage: string): Promise<IOpoSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync(message));

        try {
            const response = await action();

            if (response && response.data.success) {
                setRefreshKey(x => x + 1);
                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || failureMessage };
        } catch (err) {
            return { success: false, message: failureMessage };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const isEmailAvailable = async (email: string) => {
        const response: ITypedBaseResponse<null> = await dispatch(UserActionCreators.checkInviteEmailAvailable(email));

        return response != null && response.data.success;
    };

    const addUser = (request: IOpoUserAddRequest) => runAction(
        'Adding user, one moment please...',
        () => dispatch(AuthActionCreators.apiPost({ url: baseUrl, data: request })),
        'Unable to add the user');

    const resendVerification = (userId: number) => runAction(
        'Resending verification email, one moment please...',
        () => dispatch(AuthActionCreators.apiPost({ url: `${baseUrl}/${userId}/resend-verification` })),
        'Unable to resend the verification email');

    const updateUser = (userId: number, request: IOpoUserUpdateRequest) => runAction(
        'Saving user, one moment please...',
        () => dispatch(AuthActionCreators.apiPut({ url: `${baseUrl}/${userId}`, data: request })),
        'Unable to save the user');

    const deactivateUser = (userId: number) => runAction(
        'Deactivating user, one moment please...',
        () => dispatch(AuthActionCreators.apiPost({ url: `${baseUrl}/${userId}/deactivate` })),
        'Unable to deactivate the user');

    return { data, loading, error, isEmailAvailable, addUser, resendVerification, updateUser, deactivateUser };
}

export default useOpoPortalUsers;
